import React, { useEffect, useState } from 'react';
import { Search, ShieldAlert, Loader2, X, User as UserIcon, Trash2, Camera, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  adminSearchUsers,
  adminGetPhotoReasons,
  adminListUserPhotos,
  adminRemovePhoto,
  adminRemovePhotoUrl
} from '../../services/api';

const PhotoModerationTab = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPhotos, setUserPhotos] = useState([]); // db.photos records
  const [reasons, setReasons] = useState([]);
  const [pickedPhoto, setPickedPhoto] = useState(null); // { photo_id?, photo_url?, imageSrc }
  const [reasonCode, setReasonCode] = useState('other');
  const [adminNote, setAdminNote] = useState('');
  const [removing, setRemoving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    adminGetPhotoReasons().then(r => { setReasons(r.data); setReasonCode(r.data[0]?.code || 'other'); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      adminSearchUsers(query.trim(), { limit: 12 })
        .then(r => { if (!cancelled) setResults(r.data.users || []); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const openUser = async (u) => {
    setSelectedUser(u);
    setMessage('');
    setPickedPhoto(null);
    try {
      // Load moderation-tracked photos (db.photos collection)
      const r = await adminListUserPhotos(u.user_id);
      setUserPhotos((r.data || []).filter(p => !p.is_deleted));
    } catch {
      setUserPhotos([]);
    }
  };

  const pickTrackedPhoto = (p) => {
    setPickedPhoto({
      photo_id: p.photo_id,
      imageSrc: `${process.env.REACT_APP_BACKEND_URL}/api/photos/${p.photo_id}?variant=original`,
    });
  };
  const pickUrlPhoto = (url) => setPickedPhoto({ photo_url: url, imageSrc: url });

  const handleRemove = async () => {
    if (!pickedPhoto) return;
    if (!window.confirm(`Permanently remove this photo? Reason: ${reasons.find(r => r.code === reasonCode)?.label}`)) return;
    setRemoving(true);
    setMessage('');
    try {
      if (pickedPhoto.photo_id) {
        await adminRemovePhoto(pickedPhoto.photo_id, { reason_code: reasonCode, admin_note: adminNote });
      } else {
        await adminRemovePhotoUrl(selectedUser.user_id, {
          photo_url: pickedPhoto.photo_url,
          reason_code: reasonCode,
          admin_note: adminNote,
        });
      }
      setMessage('Photo removed. User has been notified.');
      setPickedPhoto(null);
      setAdminNote('');
      // Reload
      await openUser(selectedUser);
    } catch (e) {
      setMessage(e?.response?.data?.detail || 'Removal failed');
    } finally {
      setRemoving(false);
    }
  };

  const rawPhotoUrls = (selectedUser?.photos || []).filter(u => !userPhotos.some(p => (u || '').includes(p.photo_id)));

  return (
    <div className="space-y-4" data-testid="photo-moderation-tab">
      <div>
        <h3 className="font-semibold text-gray-900 text-lg" style={{ fontFamily: 'Outfit' }}>Photo Moderation</h3>
        <p className="text-sm text-gray-500">Search by name, email, or user ID, then review and remove any photo that breaks community rules.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users by name, email, or user ID"
            className="pl-9 rounded-xl h-11"
            data-testid="mod-user-search"
          />
        </div>
        {searching && <div className="text-xs text-gray-500 mt-2">Searching…</div>}
        {results.length > 0 && (
          <div className="mt-3 border border-gray-100 rounded-xl divide-y divide-gray-100 max-h-60 overflow-y-auto">
            {results.map(u => (
              <button
                key={u.user_id}
                onClick={() => openUser(u)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3"
                data-testid={`mod-user-${u.user_id}`}
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                  {u.photos?.[0]
                    ? <img src={u.photos[0]} alt="" className="w-full h-full object-cover" />
                    : <UserIcon className="w-4 h-4 text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{u.name || u.display_name || 'No name'}</div>
                  <div className="text-xs text-gray-500 truncate">{u.email} · {u.user_id}</div>
                </div>
                {u.photo_status === 'removed_by_admin' && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">FLAGGED</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedUser && (
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3" data-testid="mod-selected-user">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900">{selectedUser.name || selectedUser.display_name}</div>
              <div className="text-xs text-gray-500">{selectedUser.email} · {selectedUser.user_id}</div>
              <div className="text-xs text-gray-500 mt-0.5">Photo status: <span className={`font-semibold ${selectedUser.photo_status === 'removed_by_admin' ? 'text-red-600' : selectedUser.photo_status === 'uploaded' ? 'text-[#009E60]' : 'text-yellow-700'}`}>{selectedUser.photo_status || 'required'}</span></div>
            </div>
            <button onClick={() => { setSelectedUser(null); setUserPhotos([]); setPickedPhoto(null); }} className="p-1 rounded-full hover:bg-gray-100">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          <div>
            <Label className="text-sm text-gray-700 font-semibold">Photos</Label>
            {userPhotos.length === 0 && rawPhotoUrls.length === 0 ? (
              <div className="text-sm text-gray-500 mt-2 flex items-center gap-2">
                <Camera className="w-4 h-4" /> This user has no active photos.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                {userPhotos.map(p => (
                  <button
                    key={p.photo_id}
                    onClick={() => pickTrackedPhoto(p)}
                    className={`relative aspect-square rounded-xl overflow-hidden border-2 ${pickedPhoto?.photo_id === p.photo_id ? 'border-red-500' : 'border-transparent'}`}
                    data-testid={`mod-photo-${p.photo_id}`}
                  >
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/photos/${p.photo_id}?variant=original`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
                {rawPhotoUrls.map((u, i) => (
                  <button
                    key={i}
                    onClick={() => pickUrlPhoto(u)}
                    className={`relative aspect-square rounded-xl overflow-hidden border-2 ${pickedPhoto?.photo_url === u ? 'border-red-500' : 'border-transparent'}`}
                    data-testid={`mod-photo-url-${i}`}
                  >
                    <img src={u} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {pickedPhoto && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-2" data-testid="mod-removal-form">
              <div className="flex items-center gap-2 text-red-700 text-sm font-semibold">
                <ShieldAlert className="w-4 h-4" /> Remove photo
              </div>
              <div>
                <Label className="text-xs text-gray-700">Reason</Label>
                <select
                  value={reasonCode}
                  onChange={(e) => setReasonCode(e.target.value)}
                  className="w-full mt-1 h-10 rounded-lg border border-gray-200 px-2 text-sm"
                  data-testid="mod-reason-select"
                >
                  {reasons.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-gray-700">Admin note (optional)</Label>
                <Textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value.slice(0, 500))}
                  className="mt-1 min-h-[60px] rounded-lg text-sm resize-none"
                  placeholder="Internal note (not shown to user)"
                  data-testid="mod-admin-note"
                />
              </div>
              <div className="flex items-start gap-2 text-xs text-red-700">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <div>User will be notified with the reason and prompted to upload a replacement before continuing to use dating features.</div>
              </div>
              <Button
                onClick={handleRemove}
                disabled={removing}
                className="w-full h-10 bg-red-500 hover:bg-red-600 text-white rounded-full"
                data-testid="mod-remove-btn"
              >
                {removing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Remove photo
              </Button>
            </div>
          )}

          {message && (
            <div className="bg-green-50 text-green-700 border border-green-200 rounded-xl px-3 py-2 text-sm" data-testid="mod-message">{message}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoModerationTab;
